import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader, StatCard, Tag, statusTone, Loader, Empty } from "@/components/kit";
import { toast } from "sonner";
import { ShieldCheck, ShieldX, ShieldAlert, MapPin, FileCheck } from "lucide-react";

export default function Verifications() {
  const [data, setData] = useState(null);

  const load = () => api.get("/admin/verifications").then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, []);

  const verify = async (id, status) => {
    try {
      await api.post("/admin/verify", { professional_id: id, status });
      toast.success(`Professional ${status.toLowerCase()}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
  };

  if (data === null) return <Loader label="Loading verification queue" />;
  if (!data) return <Empty label="Could not load queue" />;

  return (
    <div className="fade-up">
      <PageHeader overline="Krida Verification" title="Verification Queue"
        subtitle="Review workforce credentials. Every decision is written to the audit log." testid="verifications-page" />

      <div className="grid grid-cols-2 gap-4 mb-6 max-w-md">
        <StatCard label="Pending review" value={data.pending.length} icon={ShieldAlert} accent="amber" />
        <StatCard label="Verified" value={data.verified_count} icon={ShieldCheck} accent="emerald" />
      </div>

      {data.pending.length === 0 ? <Empty label="Queue clear — all professionals reviewed" /> : (
        <div className="space-y-3">
          {data.pending.map((p) => (
            <div key={p.id} className="krida-card p-5" data-testid={`verify-row-${p.id}`}>
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <img src={p.photo} alt={p.name} className="w-14 h-14 rounded-xl object-cover border border-white/10" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold">{p.name}</span>
                    <span className="font-mono text-xs text-krida-emerald">{p.krida_id}</span>
                    <Tag tone={statusTone(p.verification_status)}>{p.verification_status}</Tag>
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-3 mt-1">
                    <span>{p.primary_role} · {p.primary_sport}</span>
                    <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {p.location?.city}</span>
                    <span className="flex items-center gap-1"><FileCheck className="w-3 h-3" /> {(p.credentials || []).length} credentials</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => verify(p.id, "Verified")} data-testid={`approve-btn-${p.id}`} className="px-4 py-2 rounded-xl bg-krida-emerald/15 text-krida-emerald text-sm font-semibold flex items-center gap-1.5 hover:bg-krida-emerald/25 transition-colors"><ShieldCheck className="w-4 h-4" /> Verify</button>
                  <button onClick={() => verify(p.id, "Rejected")} data-testid={`reject-btn-${p.id}`} className="px-4 py-2 rounded-xl bg-krida-crimson/15 text-krida-crimson text-sm font-semibold flex items-center gap-1.5 hover:bg-krida-crimson/25 transition-colors"><ShieldX className="w-4 h-4" /> Reject</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
